import type { Lead, LeadMeeting, LeadUpdatePatch } from "./leadInboxTypes";

export async function readPayload(response: Response) {
  const text = await response.text();
  let payload: any = {};
  try { payload = text ? JSON.parse(text) : {}; }
  catch { throw new Error(text.trim().startsWith("<!DOCTYPE") ? "API 서버가 HTML을 응답했습니다. Vercel dev 또는 proxy 경로를 확인하세요." : "응답을 JSON으로 읽지 못했습니다."); }
  if (!response.ok) throw new Error(payload?.error || "Lead 데이터를 불러오지 못했습니다.");
  return payload;
}

export async function fetchLeadInboxLeads(): Promise<Lead[]> {
  const payload = await readPayload(await fetch("/api/sales/leads"));
  return (payload.leads ?? []) as Lead[];
}

// 단건 조회(진단 포함) — converted 리드도 가져온다. 오퍼튜니티의 원천 lead는 converted라
// 목록(fetchLeadInboxLeads, converted 제외)으로는 못 찾으므로 제안서의 기회→진단 연결에 사용.
export async function fetchLeadById(leadId: string): Promise<Lead | null> {
  const payload = await readPayload(
    await fetch(`/api/sales/leads?leadId=${encodeURIComponent(leadId)}&includeConverted=1`)
  );
  const leads = (payload.leads ?? []) as Lead[];
  return leads[0] ?? null;
}

export async function updateLeadInboxLead(id: string, patch: LeadUpdatePatch) {
  await readPayload(await fetch("/api/sales/leads", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id,
      status: patch.status,
      // 상태 변경 이력용 — undefined 면 키가 생략되고 서버가 자동처리로 남긴다.
      status_reason: patch.statusReason,
      status_trigger: patch.statusTrigger,
      owner_name: patch.owner_name,
      priority: patch.priority,
      internal_note: patch.internalNote,
      next_action: patch.nextAction,
      next_action_due_date: patch.nextActionDueDate,
      // 기본정보 편집 가능 필드
      // ★ 키 이름은 서버 구조분해(sales-handler handleLeadsUnified PUT)와 같아야 한다.
      //   company_name 으로 보내면 서버가 못 받아 조용히 무시하고 200 을 준다(저장 안 됨).
      company: patch.company,
      website: patch.website,
      email: patch.email,
      phone: patch.phone,
      address: patch.address,
      received_at: patch.receivedAt,
      source: patch.source,
      // new fields
      lead_name: patch.leadName,
      request_type: patch.requestType,
      interest_categories: patch.interestCategories,
      target_processes: patch.targetProcesses,
      first_response_at: patch.firstResponseAt,
      raw_message: patch.rawMessage,
      summary: patch.summary,
      next_meeting_at: patch.nextMeetingAt,
      next_action_at: patch.nextActionAt,
      next_action_memo: patch.nextActionMemo,
      // 마케팅 인지 채널 — ★ undefined 면 JSON.stringify 가 키를 생략 → 서버가 기존 값 유지.
      // 채널을 다루지 않는 화면(목록 상태 변경 등)의 저장이 채널을 지우지 않는 계약의 핵심.
      inbound_channels: patch.inboundChannels,
    }),
  }));
}

export type LeadMeetingInput = {
  leadId: string;
  meetingId?: string;
  scheduledAt: string;
  memo: string;
  attachmentUrl: string;
  status: LeadMeeting["status"];
};

export async function createLeadMeeting(input: LeadMeetingInput): Promise<LeadMeeting[]> {
  const payload = await readPayload(await fetch("/api/sales/lead-meetings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      lead_id: input.leadId,
      scheduled_at: input.scheduledAt,
      memo: input.memo,
      attachment_url: input.attachmentUrl,
      status: input.status,
    }),
  }));
  return (payload.meetings ?? []) as LeadMeeting[];
}

export async function updateLeadMeeting(input: LeadMeetingInput): Promise<LeadMeeting[]> {
  const payload = await readPayload(await fetch("/api/sales/lead-meetings", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      lead_id: input.leadId,
      meeting_id: input.meetingId,
      scheduled_at: input.scheduledAt,
      memo: input.memo,
      attachment_url: input.attachmentUrl,
      status: input.status,
    }),
  }));
  return (payload.meetings ?? []) as LeadMeeting[];
}

export async function deleteLeadMeeting(input: Omit<LeadMeetingInput, "scheduledAt"> & { scheduledAt?: string }): Promise<LeadMeeting[]> {
  const payload = await readPayload(await fetch("/api/sales/lead-meetings", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      lead_id: input.leadId,
      meeting_id: input.meetingId,
      scheduled_at: input.scheduledAt,
      memo: input.memo,
      attachment_url: input.attachmentUrl,
      status: input.status,
    }),
  }));
  return (payload.meetings ?? []) as LeadMeeting[];
}

export type LeadTimelineInput = {
  leadId: string;
  id?: string;
  title: string;
  message: string;
};

export async function createLeadTimelineEvent(input: LeadTimelineInput): Promise<void> {
  await readPayload(await fetch("/api/sales/lead-timeline", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lead_id: input.leadId, title: input.title, message: input.message }),
  }));
}

export async function updateLeadTimelineEvent(input: LeadTimelineInput): Promise<void> {
  await readPayload(await fetch("/api/sales/lead-timeline", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lead_id: input.leadId, id: input.id, title: input.title, message: input.message }),
  }));
}

export async function deleteLeadTimelineEvent(input: Pick<LeadTimelineInput, "leadId" | "id">): Promise<void> {
  await readPayload(await fetch("/api/sales/lead-timeline", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lead_id: input.leadId, id: input.id }),
  }));
}

/** 축1-c: Opportunity 전환 + 대상 파이프라인 지정(선택). 미지정 시 서버가 lead_type_match→default로 결정.
 *  COL-92: Application(딜 타입) 다중선택도 전환 시 함께 저장(선택). */
export async function convertLeadInboxLead(id: string, pipelineId?: string | null, applications?: string[]) {
  // 전환 시점엔 파이프라인 + Application. deal_type(구독/유지보수)은 수주(Won) 후 "다음 단계"에서 결정.
  await readPayload(await fetch("/api/sales/leads", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id,
      status: "converted",
      pipeline_id: pipelineId || undefined,
      applications: applications && applications.length ? applications : undefined,
    }),
  }));
}

export type CreateLeadInput = {
  company: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  inquiry_type: string;
  message: string;
  solution: string;
  source: string;
  status: string;
  owner_name: string;

  priority: string;
  received_at: string;
  internal_note: string;
};

export async function createLeadInboxLead(input: CreateLeadInput): Promise<void> {
  await readPayload(await fetch("/api/sales/leads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  }));
}

export type LeadMailPreview = {
  to: string;
  from: string;
  cc: string[];
  bcc: string[];
  subject: string;
  html: string;
  delayMinutes: number;
  templateName: string;
  usedDefaultTemplate: boolean;
  ruleEnabled: boolean;
  eventType: string;
  variables: Record<string, string>;
};

export type ResendLeadMailResult = {
  recipient: string;
  subject: string;
  delayMinutes: number;
  scheduledAt: string | null;
};

/** 발송하지 않고, 알림 설정(템플릿/딜레이/from/cc/bcc)으로 구성된 메일 미리보기를 가져온다.
 *  variables: 미리보기에서 사람이 직접 고친 값 — 넘기면 서버 기본값 위에 덮어써서 다시 렌더링한다. */
export async function previewLeadInboxMail(id: string, variables?: Record<string, string>): Promise<LeadMailPreview> {
  const payload = await readPayload(await fetch("/api/sales/leads/resend-mail", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, preview: true, variables }),
  }));
  return payload.preview as LeadMailPreview;
}

/** 알림 규칙이 비활성이었을 때 못 나간 "문의 접수 확인" 메일을 수동으로 재발송한다.
 *  variables: 미리보기에서 사람이 직접 고친 값 — 넘기면 실제 발송에도 그대로 반영된다. */
export async function resendLeadInboxMail(id: string, variables?: Record<string, string>): Promise<ResendLeadMailResult> {
  const payload = await readPayload(await fetch("/api/sales/leads/resend-mail", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, variables }),
  }));
  return {
    recipient: payload.recipient,
    subject: payload.subject,
    delayMinutes: payload.delayMinutes ?? 0,
    scheduledAt: payload.scheduledAt ?? null,
  };
}
